/**
 * Выноска: карточка карты, отведённая от своей точки, метка на самой точке и
 * поводок между ними.
 *
 * Одна оболочка на все карточки карты (отсчёт, судно, снесённая точка,
 * землетрясение, косяк). До неё каждая двигалась «влево-вверх от курсора» на
 * свои сто пикселей и накрывала собой ровно то место, о котором рассказывала:
 * при ширине 244 и высоте в три сотни сдвиг в 110 никуда с точки не уводил.
 *
 * ПРИВЯЗКА ГЕОГРАФИЧЕСКАЯ, а не пиксельная. Пиксель клика перестаёт значить
 * что-либо, как только карту сдвинули: раньше это было незаметно (карточка
 * просто висела где висела), но метка с поводком, оставшись на месте, показывали
 * бы пальцем в чужую воду. Поэтому здесь хранится lng/lat, а пиксель считается
 * заново на каждое движение карты.
 *
 * Размер карточки ЗАМЕРЯЕТСЯ, а не берётся из константы: содержимое приезжает
 * запросом, и высота у одной и той же карточки разная. Замер идёт в
 * `useLayoutEffect` — до отрисовки кадра, поэтому карточка не прыгает.
 *
 * Карточку можно УВЕСТИ мышью за ручку (`data-callout-handle` внутри неё):
 * правило непокрытия отводит её само, но когда человеку нужна вода именно под
 * ней, решает он. Тащим только за ручку, а не за всю карточку: в теле лежат
 * MMSI, номер ИМО и владелец, и захват на всём полотне отнял бы у них выделение
 * мышью. Метка при этом остаётся на точке, а поводок тянется до нового места.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { CALLOUT_MARKER_R, anchorLng, dragCallout, placeCallout } from './callout'
import { getActiveMap, subscribeActiveMap } from './activeMap'

interface Props {
  /** Точка, о которой карточка рассказывает. */
  anchor: { lng: number; lat: number }
  /** Ширина карточки в пикселях. */
  width: number
  /** Закреплена кликом — ловит мышь. Читалка при наведении для мыши прозрачна:
   *  наползая на курсор, она уводила бы его с точки и гасила сама себя. */
  pinned?: boolean
  /** Карточку можно увести с её места мышью — за элемент с
   *  `data-callout-handle` внутри неё. */
  draggable?: boolean
  children: ReactNode
}

export function MapCallout({ anchor, width, pinned = true, draggable = false, children }: Props) {
  const map = useSyncExternalStore(subscribeActiveMap, getActiveMap)
  const boxRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState({ dx: 0, dy: 0 })
  const [dragging, setDragging] = useState(false)
  // Откуда потащили: пиксель курсора и смещение, которое уже было набрано.
  const grab = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null)
  const [height, setHeight] = useState(0)
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  // Счётчик движений карты: пиксель привязки считается в отрисовке, а отрисовку
  // надо чем-то позвать. `move` покрывает и пан, и зум, и полёт.
  const [, bump] = useState(0)

  // Новая точка — новая карточка: место, куда увели прошлую, к ней отношения не
  // имеет. Ключа у карточек карты нет (см. cardPick.ts), компонент
  // переиспользуется, и без сброса карточка нового судна встала бы там, куда
  // человек оттащил предыдущую.
  useEffect(() => {
    setDrag({ dx: 0, dy: 0 })
  }, [anchor.lng, anchor.lat])

  useLayoutEffect(() => {
    if (!map) return
    const onMove = () => bump(v => v + 1)
    map.on('move', onMove)
    map.on('resize', onMove)
    return () => {
      map.off('move', onMove)
      map.off('resize', onMove)
    }
  }, [map])

  // Замер без списка зависимостей: высота меняется от приехавших данных, а
  // размер полотна — от окна и рельса разделов. Состояние трогаем только при
  // расхождении, иначе замер звал бы сам себя.
  useLayoutEffect(() => {
    const el = boxRef.current
    const parent = el?.offsetParent as HTMLElement | null
    if (!el || !parent) return
    if (el.offsetHeight !== height) setHeight(el.offsetHeight)
    if (parent.clientWidth !== viewport.width || parent.clientHeight !== viewport.height) {
      setViewport({ width: parent.clientWidth, height: parent.clientHeight })
    }
  })

  const point = map
    ? map.project([anchorLng(anchor.lng, map.getCenter().lng), anchor.lat])
    : null
  // Полотно ещё не замерено (первая отрисовка) — берём его размер у карты:
  // карточка обязана встать сразу, промежуточного кадра «в углу» быть не должно.
  const view =
    viewport.width > 0
      ? viewport
      : map
        ? { width: map.getContainer().clientWidth, height: map.getContainer().clientHeight }
        : { width: 0, height: 0 }

  const input =
    point && view.width > 0
      ? { anchor: { x: point.x, y: point.y }, size: { width, height }, viewport: view }
      : null
  // Нулевое смещение возвращает базовое размещение как есть: карточка, которую
  // не трогали, ведёт себя ровно как до появления перетаскивания.
  const place = input ? dragCallout(placeCallout(input), drag, input) : null

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggable || !pinned) return
    const target = event.target as HTMLElement
    if (!target.closest('[data-callout-handle]')) return
    // Кнопка внутри ручки остаётся кнопкой: захват увёл бы у неё клик.
    if (target.closest('button, a, input, select, textarea')) return
    // Карта под карточкой не должна поехать следом за курсором.
    event.stopPropagation()
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    grab.current = { x: event.clientX, y: event.clientY, dx: drag.dx, dy: drag.dy }
    setDragging(true)
  }

  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const from = grab.current
    if (!from) return
    setDrag({ dx: from.dx + event.clientX - from.x, dy: from.dy + event.clientY - from.y })
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!grab.current) return
    grab.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <>
      {/* Метка и поводок. Белая подложка под обеими линиями — приём читаемости
          поверх чужой картинки (растр слоя, спутниковая подложка), общий на все
          бренды, а не айдентика: см. реестр палитр. */}
      {place && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <line
            x1={place.line.x1}
            y1={place.line.y1}
            x2={place.line.x2}
            y2={place.line.y2}
            className="stroke-white/85"
            strokeWidth={4}
            strokeLinecap="round"
          />
          <line
            x1={place.line.x1}
            y1={place.line.y1}
            x2={place.line.x2}
            y2={place.line.y2}
            className="stroke-sonar"
            strokeWidth={1.75}
            strokeLinecap="round"
          />
          <circle
            cx={point!.x}
            cy={point!.y}
            r={CALLOUT_MARKER_R}
            fill="none"
            className="stroke-white/85"
            strokeWidth={4}
          />
          <circle
            cx={point!.x}
            cy={point!.y}
            r={CALLOUT_MARKER_R}
            fill="none"
            className="stroke-sonar"
            strokeWidth={2}
          />
          <circle cx={point!.x} cy={point!.y} r={1.75} className="fill-sonar" />
        </svg>
      )}
      <div
        ref={boxRef}
        onPointerDown={draggable ? startDrag : undefined}
        onPointerMove={draggable ? moveDrag : undefined}
        onPointerUp={draggable ? endDrag : undefined}
        onPointerCancel={draggable ? endDrag : undefined}
        style={{
          left: place?.left ?? -9999,
          top: place?.top ?? -9999,
          width,
          // Тащим пальцем — а не прокручиваем страницу и не двигаем карту.
          touchAction: draggable ? 'none' : undefined,
        }}
        className={`absolute rounded-xl border border-hull bg-deep p-3 shadow-console ${
          pinned ? 'pointer-events-auto' : 'pointer-events-none'
        } ${dragging ? 'select-none' : ''}`}
      >
        {children}
      </div>
    </>
  )
}
